import { useEffect, useState } from "react";
import { Box, Menu, MenuItem, Typography } from "@mui/material";
import { useShallow } from "zustand/react/shallow";

import AddIcon from "@mui/icons-material/Add";

import globalStyles from "../../../../assets/styles/global.module.scss";
import styles from "../../../../assets/styles/componentStyles/templateLibraryStyles/whatsappTemplatePreview.module.scss";

import WrapWithHoverUI from "../utils/WrapWithHoverUI";
import { ALLOWED_MEDIA_HEADER_TYPES_CAROUSEL, headerTypeIconMap, isTemplateCreated } from "../constant";
import { useTemplateLibraryStore } from "../../../../store/templateLibraryStore";

const CarouselTemplateMediaHeaderPreview = (props: any) => {

    const { editable = false, handleChangeCardMediaHeader } = props;

    const { createTemplatePayload } = useTemplateLibraryStore(useShallow((state) => ({
        createTemplatePayload: state.createTemplatePayload,
    })));

    const whatsappTemplateHeaderTypes = useTemplateLibraryStore((state) => state.whatsappTemplateHeaderTypes);
    
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    // Constants
    const carouselCards = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_card ?? [];
    const getCardHeaderTypeName = (card: any) => card?.whatsapp_template_header?.whatsapp_template_header_type_name?.toLowerCase() ?? "";

    const hasHeader = carouselCards.length > 0 && carouselCards.every((card) =>
        ALLOWED_MEDIA_HEADER_TYPES_CAROUSEL.includes(getCardHeaderTypeName(card))
    );

    const selectedMediaHeaderType = carouselCards.find((card) =>
        ALLOWED_MEDIA_HEADER_TYPES_CAROUSEL.includes(getCardHeaderTypeName(card))
    )?.whatsapp_template_header?.whatsapp_template_header_type_name ?? "";

    const headerType = selectedMediaHeaderType.toLowerCase();

    const mediaHeaderOptions = whatsappTemplateHeaderTypes?.filter((headerTypeOption: any) =>
        ALLOWED_MEDIA_HEADER_TYPES_CAROUSEL.includes(headerTypeOption?.whatsapp_template_header_type_name?.toLowerCase())
    ) ?? [];

    const isHeaderOptionSelected = (optionName: string) => selectedMediaHeaderType?.toLowerCase() === optionName?.toLowerCase();

    // Functions
    const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (isTemplateCreated(createTemplatePayload)) return;
        setAnchorEl(e.currentTarget);
    };

    const handleCloseMenu = () => setAnchorEl(null);

    const handleSelectType = (headers: any) => {
        handleChangeCardMediaHeader(headers);
        handleCloseMenu();
    };

    const renderCardMediaHeaderContent = () => {
        const typeIcon = headerTypeIconMap[headerType as keyof typeof headerTypeIconMap];

        return (
            <Box
                onClick={editable && !isTemplateCreated(createTemplatePayload) ? handleOpenMenu : undefined}
                className={`${styles.whatsappPreview__cardMediaHeaderType} ${globalStyles.clickable}`}
                dir="auto"
            >
                {typeIcon && (
                    <span className={styles.whatsappPreview__cardMediaHeaderType__icon}>
                        {typeIcon}
                    </span>
                )}

                <Typography component="span" className={styles.whatsappPreview__cardMediaHeaderType__text}>
                    {selectedMediaHeaderType}
                </Typography>
            </Box>
        );
    };

    const wrapWithHoverUI = (content: React.ReactNode) => (
        <WrapWithHoverUI
            label="Card Media Header"
            editable={editable}
            showEditIcon={hasHeader && editable && !isTemplateCreated(createTemplatePayload)}
            onEditClick={editable ? handleOpenMenu : undefined}
            onHoverChange={() => {}}
            containerClassName={styles.whatsappPreview__carouselOutsideMediaHeader}
            editContent={
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    {mediaHeaderOptions?.map((headers: any) => (
                        <MenuItem
                            key={headers?.whatsapp_template_header_type_id}
                            onClick={() => handleSelectType(headers)}
                            disabled={isTemplateCreated(createTemplatePayload)}
                            className={isHeaderOptionSelected(headers?.whatsapp_template_header_type_name) ? styles.whatsappPreview__headerTypeMenuItemSelected : undefined}
                        >
                            <Box className={`${styles.whatsappPreview__headerTypeMenuItem} ${globalStyles.clickable}`}>
                                {headerTypeIconMap[headers?.whatsapp_template_header_type_name?.toLowerCase() as keyof typeof headerTypeIconMap] ?? null}
                                <span>
                                    {headers?.whatsapp_template_header_type_name}
                                </span>
                            </Box>
                        </MenuItem>
                    ))}
                </Menu>
            }
        >
            {content}
        </WrapWithHoverUI>
    );

    useEffect(() => {
        setAnchorEl(null);
    }, []);

    if (!hasHeader) {
        return wrapWithHoverUI(
            <Box
                onClick={handleOpenMenu}
                className={`${styles.whatsappPreview__addHeader} ${globalStyles.clickable} ${styles.whatsappPreview__textInputError}`}
            >
                <AddIcon fontSize="small" />
                <Typography variant="body2">Add Card Media Header</Typography>
            </Box>
        );
    }

    return wrapWithHoverUI(renderCardMediaHeaderContent());
};

export default CarouselTemplateMediaHeaderPreview;